import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import { CommunityBlueprint } from '../components/CommunityBlueprint';

const EditProfileModal = ({ profile, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    display_name: profile?.display_name || '',
    bio: profile?.bio || '',
    location: profile?.location || '',
    website: profile?.website || '',
    birthday: profile?.birthday || '',
    zodiac: profile?.zodiac || '',
    mbti: profile?.mbti || '',
    socials: profile?.socials || { x: '', instagram: '', youtube: '', tiktok: '', substack: '', facebook: '' }
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('social_')) {
      const socialKey = name.replace('social_', '');
      setFormData(prev => ({
        ...prev,
        socials: { ...prev.socials, [socialKey]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg rounded-3xl border border-white/10 bg-slate-900 p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-6">Edit Profile</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex items-center gap-4 p-4 bg-white/5 rounded-xl border border-white/10">
            <div className="w-16 h-16 rounded-full bg-gray-700 overflow-hidden flex-shrink-0">
              {profile?.avatar_url ? (
                <img src={profile.avatar_url} alt="Avatar" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-2xl">👤</div>
              )}
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-slate-400 mb-2">Profile Picture</label>
              <input
                type="file"
                accept="image/*"
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (file) {
                    try {
                      await apiService.uploadAvatar(file);
                      alert('Avatar uploaded! It will appear after you save changes.');
                    } catch (err) {
                      console.error(err);
                      alert('Failed to upload avatar');
                    }
                  }
                }}
                className="block w-full text-sm text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700 cursor-pointer"
              />
            </div>
          </div>

          <div>
            <label htmlFor="display_name" className="block text-sm font-medium text-slate-400 mb-1">Display Name</label>
            <input
              id="display_name"
              type="text"
              name="display_name"
              value={formData.display_name}
              onChange={handleChange}
              className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-2 text-white focus:border-blue-500 focus:outline-none"
            />
          </div>
          <div>
            <label htmlFor="bio" className="block text-sm font-medium text-slate-400 mb-1">Bio</label>
            <textarea
              id="bio"
              name="bio"
              value={formData.bio}
              onChange={handleChange}
              rows={3}
              className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-2 text-white focus:border-blue-500 focus:outline-none"
            />
          </div>
          
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label htmlFor="birthday" className="block text-sm font-medium text-slate-400 mb-1">Birthday</label>
              <input
                id="birthday"
                type="date"
                name="birthday"
                value={formData.birthday}
                onChange={handleChange}
                className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-2 text-white focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label htmlFor="zodiac" className="block text-sm font-medium text-slate-400 mb-1">Zodiac</label>
              <select
                id="zodiac"
                name="zodiac"
                value={formData.zodiac}
                onChange={handleChange}
                className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-2 text-white focus:border-blue-500 focus:outline-none"
              >
                <option value="">Select</option>
                {['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'].map(z => (
                  <option key={z} value={z}>{z}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="mbti" className="block text-sm font-medium text-slate-400 mb-1">MBTI</label>
              <select
                id="mbti"
                name="mbti"
                value={formData.mbti}
                onChange={handleChange}
                className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-2 text-white focus:border-blue-500 focus:outline-none"
              >
                <option value="">Select</option>
                {['INTJ', 'INTP', 'ENTJ', 'ENTP', 'INFJ', 'INFP', 'ENFJ', 'ENFP', 'ISTJ', 'ISFJ', 'ESTJ', 'ESFJ', 'ISTP', 'ISFP', 'ESTP', 'ESFP'].map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="location" className="block text-sm font-medium text-slate-400 mb-1">Location</label>
              <div className="flex gap-2">
                <input
                  id="location"
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-2 text-white focus:border-blue-500 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => {
                    if (navigator.geolocation) {
                      navigator.geolocation.getCurrentPosition(async (position) => {
                        const { latitude, longitude } = position.coords;
                        // In a real app, we'd reverse geocode this. For now, store coords or a placeholder
                        // Or call an API to get city/country
                        try {
                          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`);
                          const data = await res.json();
                          const loc = data.address.city || data.address.town || data.address.country || `${latitude}, ${longitude}`;
                          setFormData(prev => ({ ...prev, location: loc }));
                        } catch (e) {
                          setFormData(prev => ({ ...prev, location: `${latitude.toFixed(2)}, ${longitude.toFixed(2)}` }));
                        }
                      }, (err) => {
                        console.error(err);
                        alert('Could not get location. Please enable permissions.');
                      });
                    } else {
                      alert('Geolocation is not supported by this browser.');
                    }
                  }}
                  className="px-3 py-2 bg-blue-600 rounded-xl hover:bg-blue-500"
                  title="Use Current Location"
                >
                  📍
                </button>
              </div>
            </div>
            <div>
              <label htmlFor="website" className="block text-sm font-medium text-slate-400 mb-1">Website</label>
              <input
                id="website"
                type="url"
                name="website"
                value={formData.website}
                onChange={handleChange}
                className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-2 text-white focus:border-blue-500 focus:outline-none"
              />
            </div>
          </div>
          
          <div className="pt-4 border-t border-white/10">
            <h3 className="text-sm font-bold text-white mb-3">Social Links</h3>
            <div className="space-y-3">
              {['x', 'instagram', 'youtube', 'tiktok', 'substack', 'facebook'].map(platform => (
                <div key={platform} className="flex items-center gap-3">
                  <span className="w-20 text-xs uppercase text-slate-500">{platform}</span>
                  <input
                    aria-label={`${platform} link`}
                    type="text"
                    name={`social_${platform}`}
                    value={formData.socials[platform] || ''}
                    onChange={handleChange}
                    placeholder={`@username or URL`}
                    className="flex-1 rounded-xl border border-white/10 bg-black/20 px-4 py-2 text-sm text-white focus:border-blue-500 focus:outline-none"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="pt-4 border-t border-white/10">
             <h3 className="text-sm font-bold text-white mb-3">Auto Import</h3>
             <p className="text-xs text-slate-400 mb-3">Connect accounts to automatically import your content.</p>
             <div className="flex gap-2">
                <button type="button" className="px-4 py-2 rounded-xl bg-black text-white text-sm font-bold hover:bg-gray-900 border border-white/10">X</button>
                <button type="button" className="px-4 py-2 rounded-xl bg-[#E1306C]/20 text-[#E1306C] text-sm font-bold hover:bg-[#E1306C]/30">Instagram</button>
                <button type="button" className="px-4 py-2 rounded-xl bg-[#FF0000]/20 text-[#FF0000] text-sm font-bold hover:bg-[#FF0000]/30">YouTube</button>
                <button type="button" className="px-4 py-2 rounded-xl bg-[#00f2ea]/20 text-[#00f2ea] text-sm font-bold hover:bg-[#00f2ea]/30">TikTok</button>
             </div>
          </div>

          <div className="flex justify-end gap-3 pt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 rounded-xl text-slate-400 hover:text-white hover:bg-white/5"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-500"
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const PostItem = ({ post, viewingOwnProfile, onDownload, onDelete }) => {
  const videoRef = React.useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const handlePlayClick = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <div className="group relative aspect-square rounded-2xl border border-white/10 bg-slate-900/80 overflow-hidden">
      <video
        ref={videoRef}
        src={post.content_url}
        className="w-full h-full object-cover cursor-pointer"
        preload="metadata"
        playsInline
        loop
        onClick={handlePlayClick}
        onEnded={() => setIsPlaying(false)}
      />
      
      {/* Play Icon - Only show when paused */}
      {!isPlaying && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-12 h-12 rounded-full bg-black/40 flex items-center justify-center text-white text-2xl backdrop-blur-sm">
            ▶
          </div>
        </div>
      )}

      {/* Action Buttons - Top Right */}
      {viewingOwnProfile && (
        <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <button
            onClick={(e) => { e.stopPropagation(); onDownload(post.content_url, `post-${post.id}.mp4`); }}
            className="p-2 rounded-full bg-black/60 hover:bg-black/80 text-white backdrop-blur-md"
            title="Download"
          >
            ⬇️
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(post.id); }}
            className="p-2 rounded-full bg-red-500/80 hover:bg-red-600 text-white backdrop-blur-md"
            title="Delete"
          >
            🗑️
          </button>
        </div>
      )}
    </div>
  );
};

export function Profile() {
  const { username } = useParams();
  const { user: currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [artworks, setArtworks] = useState([]);
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('posts');
  const [community, setCommunity] = useState(null);
  const [communityLoading, setCommunityLoading] = useState(true);
  const [communityError, setCommunityError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const handleSaveProfile = async (updates) => {
    try {
      await apiService.updateProfile(updates);
      setProfile(prev => ({ ...prev, ...updates }));
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update profile:', error);
      alert('Failed to save changes. Please try again.');
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    try {
      await apiService.deletePost(postId);
      setPosts(prev => prev.filter(p => p.id !== postId));
    } catch (error) {
      console.error('Failed to delete post:', error);
    }
  };

  const handleDownloadPost = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'video.mp4';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const loadCommunitySection = useCallback(async (handle) => {
    if (!handle) {
      setCommunity(null);
      setCommunityLoading(false);
      return;
    }

    try {
      setCommunityLoading(true);
      setCommunityError(null);
      const response = await apiService.getUserCommunity(handle);
      setCommunity(response.community);
    } catch (error) {
      console.warn('Community lookup failed:', error);
      setCommunity(null);
      setCommunityError(
        error?.message?.includes('404')
          ? 'This creator has not configured a community yet.'
          : 'Unable to load community details right now.'
      );
    } finally {
      setCommunityLoading(false);
    }
  }, []);

  const viewerUsername = currentUser?.username;

  const loadProfile = useCallback(async () => {
    try {
      setLoading(true);
      // If no username in URL or username is 'undefined', use current user's username
      let profileUsername = username;
      if (!profileUsername || profileUsername === 'undefined' || profileUsername === 'me') {
        profileUsername = viewerUsername;
      }
      
      if (!profileUsername) {
        console.error('No username available');
        setLoading(false);
        return;
      }

      console.log('Loading profile for:', profileUsername);

      const [profileData, userPosts] = await Promise.all([
        apiService.getProfile(profileUsername),
        apiService.getUserPosts(profileUsername, { limit: 20 })
      ]);
      
      setProfile(profileData);
      setPosts(userPosts);
      
      // Load artworks and stories in background
      try {
        const artResponse = await apiService.request(`/api/art/artist/${profileData.id || profileData.user_id}`);
        setArtworks(artResponse?.artworks || []);
      } catch (err) {
        console.warn('Failed to load artworks:', err);
        setArtworks([]);
      }
      
      try {
        const storiesResponse = await apiService.request(`/api/stories/author/${profileData.id || profileData.user_id}`);
        setStories(storiesResponse?.stories || []);
      } catch (err) {
        console.warn('Failed to load stories:', err);
        setStories([]);
      }
      
      await loadCommunitySection(profileUsername);
    } catch (error) {
      console.error('Failed to load profile:', error);
    } finally {
      setLoading(false);
    }
  }, [username, viewerUsername, loadCommunitySection]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen px-6 text-center">
        <div className="rounded-2xl border border-white/10 bg-white/5 px-6 py-4 text-white/80">
          Loading profile…
        </div>
      </div>
    );
  }

  const displayName = profile?.display_name || profile?.name || username || 'User';
  const displayUsername = profile?.username || username || 'unknown';
  const viewingOwnProfile = Boolean(
    currentUser?.username && profile?.username && currentUser.username.toLowerCase() === profile.username.toLowerCase()
  );
  const handleCommunityRefresh = () => {
    const handle = profile?.username || username || currentUser?.username;
    if (handle) {
      loadCommunitySection(handle);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-4 py-8 space-y-8">
      <div className="rounded-3xl border border-white/10 bg-white/5 p-6 sm:p-8 text-center">
        <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-3xl font-bold mx-auto mb-4 shadow-lg shadow-purple-900/40">
          {displayName?.[0]?.toUpperCase() || 'U'}
        </div>
        <h1 className="text-2xl font-bold">{displayName}</h1>
        <p className="text-slate-400 mt-1">@{displayUsername}</p>
        
        <div className="flex flex-wrap justify-center gap-3 mt-3 text-xs text-slate-400">
          {profile?.birthday && (
            <span className="px-2 py-1 rounded-full bg-white/5 border border-white/10">
              🎂 {new Date(profile.birthday).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
            </span>
          )}
          {profile?.zodiac && (
            <span className="px-2 py-1 rounded-full bg-white/5 border border-white/10">
              ✨ {profile.zodiac}
            </span>
          )}
          {profile?.mbti && (
            <span className="px-2 py-1 rounded-full bg-white/5 border border-white/10">
              🧠 {profile.mbti}
            </span>
          )}
        </div>

        {profile?.bio && (
          <p className="text-slate-300 mt-4 max-w-md mx-auto">{profile.bio}</p>
        )}
        
        {viewingOwnProfile && (
          <button
            onClick={() => setIsEditing(true)}
            className="mt-4 px-4 py-1.5 rounded-full bg-white/10 text-sm font-medium hover:bg-white/20 transition"
          >
            Edit Profile
          </button>
        )}

        <div className="mt-8 grid grid-cols-3 gap-3 max-w-md mx-auto text-center text-sm uppercase tracking-wide text-white/70">
          <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3">
            <div className="text-2xl font-bold text-white">{profile?.post_count || posts?.length || 0}</div>
            <div className="text-xs text-slate-400">Posts</div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3">
            <div className="text-2xl font-bold text-white">{profile?.follower_count || 0}</div>
            <div className="text-xs text-slate-400">Followers</div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3">
            <div className="text-2xl font-bold text-white">{profile?.following_count || 0}</div>
            <div className="text-xs text-slate-400">Following</div>
          </div>
        </div>
      </div>

        <div className="mt-4 flex flex-wrap gap-4 border-b border-white/10">
          {[
            { id: 'posts', label: 'Posts' },
            { id: 'art', label: 'Art' },
            { id: 'stories', label: 'Stories' },
            { id: 'community', label: 'Community' },
            { id: 'audio', label: 'Audio' },
            { id: 'music', label: 'Music' },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`pb-3 text-sm font-semibold tracking-wide uppercase transition ${
                activeTab === tab.id
                  ? 'text-white border-b-2 border-white'
                  : 'text-white/40 hover:text-white/80'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'posts' && (
          <div className="mt-8">
            {posts.length > 0 ? (
              <>
                <h2 className="text-xl font-bold mb-4">Posts</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {posts.map((post) => (
                    <PostItem
                      key={post.id}
                      post={post}
                      viewingOwnProfile={viewingOwnProfile}
                      onDownload={handleDownloadPost}
                      onDelete={handleDeletePost}
                    />
                  ))}
                </div>
              </>
            ) : (
              <div className="rounded-2xl border border-dashed border-white/15 bg-white/5 px-6 py-8 text-center text-white/70">
                No posts yet. When this creator shares something, it will appear here.
              </div>
            )}
          </div>
        )}

        {(activeTab === 'audio' || activeTab === 'music') && (
          <div className="mt-8 rounded-2xl border border-dashed border-white/15 bg-white/5 px-6 py-12 text-center">
            <div className="text-4xl mb-4">🎵</div>
            <h3 className="text-xl font-bold text-white mb-2">
              {activeTab === 'audio' ? 'Audio Clips' : 'Music Library'}
            </h3>
            <p className="text-slate-400 max-w-md mx-auto">
              {viewingOwnProfile 
                ? `You haven't uploaded any ${activeTab} yet. Connect your accounts to import content.`
                : `This user hasn't shared any ${activeTab} yet.`}
            </p>
            {viewingOwnProfile && (
              <button 
                onClick={() => setIsEditing(true)}
                className="mt-6 px-6 py-2 rounded-xl bg-blue-600/20 text-blue-200 font-bold hover:bg-blue-600/30"
              >
                Manage Imports
              </button>
            )}
          </div>
        )}

        {activeTab === 'art' && (
          <div className="mt-8">
            {artworks.length > 0 ? (
              <>
                <h2 className="text-xl font-bold mb-4">Artwork Gallery</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {artworks.map((artwork) => (
                    <div key={artwork.id} className="group relative aspect-square rounded-2xl border border-white/10 bg-slate-900/80 overflow-hidden">
                      <img
                        src={artwork.thumbnail_url || artwork.image_url}
                        alt={artwork.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="absolute bottom-0 left-0 right-0 p-3">
                          <p className="text-white font-semibold text-sm truncate">{artwork.title}</p>
                          <p className="text-white/60 text-xs">{artwork.art_type?.replace('_', ' ')}</p>
                          <div className="flex items-center gap-3 mt-1 text-xs text-white/50">
                            <span>❤️ {artwork.like_count || 0}</span>
                            <span>👁️ {artwork.view_count || 0}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="rounded-2xl border border-dashed border-white/15 bg-white/5 px-6 py-12 text-center">
                <div className="text-4xl mb-4">🎨</div>
                <h3 className="text-xl font-bold text-white mb-2">Art Gallery</h3>
                <p className="text-slate-400 max-w-md mx-auto">
                  {viewingOwnProfile 
                    ? "You haven't created any artwork yet. Head to the Art Studio to start creating!"
                    : "This user hasn't shared any artwork yet."}
                </p>
                {viewingOwnProfile && (
                  <a 
                    href="/create?mode=art"
                    className="inline-block mt-6 px-6 py-2 rounded-xl bg-purple-600/20 text-purple-200 font-bold hover:bg-purple-600/30"
                  >
                    Open Art Studio
                  </a>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'stories' && (
          <div className="mt-8">
            {stories.length > 0 ? (
              <>
                <h2 className="text-xl font-bold mb-4">Stories & Writing</h2>
                <div className="space-y-4">
                  {stories.map((story) => (
                    <div key={story.id} className="group rounded-2xl border border-white/10 bg-slate-900/80 p-4 hover:bg-slate-800/80 transition">
                      <div className="flex items-start gap-4">
                        {story.cover_image_url && (
                          <img
                            src={story.cover_image_url}
                            alt={story.title}
                            className="w-20 h-28 object-cover rounded-lg flex-shrink-0"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-bold text-white truncate">{story.title}</h3>
                          {story.subtitle && (
                            <p className="text-white/60 text-sm mt-1">{story.subtitle}</p>
                          )}
                          <p className="text-slate-400 text-sm mt-2 line-clamp-2">{story.description || story.synopsis}</p>
                          <div className="flex items-center gap-4 mt-3 text-xs text-white/50">
                            <span className="px-2 py-1 rounded-full bg-white/5">{story.genre || 'General'}</span>
                            <span>📖 {story.chapter_count || 0} chapters</span>
                            <span>❤️ {story.like_count || 0}</span>
                            <span>👁️ {story.view_count || 0}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="rounded-2xl border border-dashed border-white/15 bg-white/5 px-6 py-12 text-center">
                <div className="text-4xl mb-4">📚</div>
                <h3 className="text-xl font-bold text-white mb-2">Stories & Writing</h3>
                <p className="text-slate-400 max-w-md mx-auto">
                  {viewingOwnProfile 
                    ? "You haven't published any stories yet. Head to the Writer's Studio to start writing!"
                    : "This user hasn't shared any stories yet."}
                </p>
                {viewingOwnProfile && (
                  <a 
                    href="/create?mode=writer"
                    className="inline-block mt-6 px-6 py-2 rounded-xl bg-amber-600/20 text-amber-200 font-bold hover:bg-amber-600/30"
                  >
                    Open Writer's Studio
                  </a>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'community' && (
          <div className="mt-8 rounded-3xl border border-white/10 bg-white/5 p-4 sm:p-6">
            {communityLoading && (
              <div className="text-center text-white/70">Loading community...</div>
            )}

            {!communityLoading && community && (
              <>
                {viewingOwnProfile && (
                  <div className="mb-4 rounded-2xl border border-white/15 bg-black/20 px-4 py-3 text-xs text-white/70">
                    Toggle “Customize layout” to show/hide rooms on your public profile, or add bespoke channels
                    right from here.
                  </div>
                )}
                <CommunityBlueprint
                  community={community}
                  canCustomize={viewingOwnProfile}
                  initialPreview={viewingOwnProfile ? 'owner' : undefined}
                  onChannelSelect={viewingOwnProfile ? undefined : (channel) => console.log('Open channel', channel)}
                  compact={!viewingOwnProfile}
                  channelsLoading={communityLoading}
                  onChannelMutation={viewingOwnProfile ? handleCommunityRefresh : undefined}
                  onScaffoldComplete={viewingOwnProfile ? handleCommunityRefresh : undefined}
                />
              </>
            )}

            {!communityLoading && !community && (
              <div className="rounded-2xl border border-dashed border-white/15 bg-black/20 px-6 py-8 text-center text-white/70">
                {communityError || 'No community has been published for this profile yet.'}
              </div>
            )}
          </div>
        )}

      {isEditing && (
        <EditProfileModal
          profile={profile}
          onClose={() => setIsEditing(false)}
          onSave={handleSaveProfile}
        />
      )}
    </div>
  );
}

export default Profile;
